/** Load and normalize PDF, structured, or plain-text evidence. */

import { useEffect, useState } from "react";
import type { StructuredEvidenceDocumentResponse } from "../../../../api/clinicalTrialsApiTypes";
import { clinicalTrialsService } from "../../../../api/clinicalTrialsService";
import { downloadEvidence } from "../utils/evidenceSourceUtils";

/** State exposed to the evidence viewer. */
export interface EvidenceSourceState {
  pdfUrl: string | null;
  structuredDocument: StructuredEvidenceDocumentResponse | null;
  textDocument: string | null;
  loading: boolean;
  error: string;
}

/** Fetch an evidence source and clean up its request and generated PDF URL. */
export function useEvidenceSource(sourcePath: string): EvidenceSourceState {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [structuredDocument, setStructuredDocument] =
    useState<StructuredEvidenceDocumentResponse | null>(null);
  const [textDocument, setTextDocument] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let pdfObjectUrl: string | null = null;
    setLoading(true);
    setError("");
    setPdfUrl(null);
    setStructuredDocument(null);
    setTextDocument(null);

    const loadEvidence = async () => {
      try {
        const response = await clinicalTrialsService.getEligibilitySourcePdf(
          sourcePath,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        if (typeof response === "string") {
          const text = response.trim();
          if (!text) throw new Error("The document source returned empty text");
          setTextDocument(text);
          return;
        }

        if (!("url" in response)) {
          setStructuredDocument(response);
          return;
        }

        const downloaded = await downloadEvidence(
          response.url,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        if (downloaded.kind === "structured") {
          setStructuredDocument(downloaded.document);
        } else if (downloaded.kind === "text") {
          setTextDocument(downloaded.text);
        } else {
          pdfObjectUrl = URL.createObjectURL(downloaded.blob);
          setPdfUrl(pdfObjectUrl);
        }
      } catch (reason: unknown) {
        if (!controller.signal.aborted) {
          setError(
            reason instanceof Error ? reason.message : "Unable to load document",
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void loadEvidence();
    return () => {
      controller.abort();
      if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
    };
  }, [sourcePath]);

  return { pdfUrl, structuredDocument, textDocument, loading, error };
}
